import React, { useCallback, useEffect, useState } from 'react';
import { useRouter } from 'next/router';
import cx from 'classnames';
import { Button } from 'phoenix-components';
import styles from './paymentLink.module.css';
import Header from 'components/orders/Header';
import { FooterButton } from 'components/common/FooterButton';
import { RadioCheckedIcon, RadioIcon } from 'components/shared/Icons/radio';
import PropTypes from 'prop-types';
import { useSyncShop } from 'hooks/shop';
import Meta from 'components/seo/Meta';
import PaymentLinkCard from './paymentLinkCard';
import { getCustomModeName, getCustomModes } from './paymentUtils';
import { useIsDesktop } from 'contexts/profile';
import StoreHeader from 'components/store/Header';
import { Container } from 'components/layout/Container';
import { Divider } from '@mui/material';
import ThankYou from './ThankYou';
import { Loader, SnackBar } from 'ui/services';
import { payForLink, payForLinkPoll } from 'ui/api/orders';
import { getUrl } from 'ui/utils';
import Payments from 'ui/services/payments';
import { emitEvent, EVENT_TYPES } from 'ui/lib/pixels';
import { useQueryParams } from 'hooks/location';
import { useIsCustomDomain } from 'contexts/profile';
import { CustomError } from 'components/error/Error';
import Skeleton from 'components/layouts/Skeleton';

export default function PaymentLink({
  shop,
  link
}) {
  useSyncShop(shop);

  const {
    orderId,
    amount,
    modes,
    status,
  } = link;

  const history = useRouter();
  const isDesktop = useIsDesktop();
  const [selectedMode, setSelectedMode] = useState(modes?.[0]);
  const [receipt, setReceipt] = useState('');
  const [thankYou, setThankYou] = useState(false);
  const params = useQueryParams();
  const isCustomDomain = useIsCustomDomain();

  const checkStatus = useCallback(async (pgTid, tid, interval) => {
    try {
      Loader.show();
      const { status } = await payForLinkPoll(link._id, pgTid, tid);
      if (status === 'received') {
        setThankYou(true);
        if (interval) {
          clearInterval(interval);
        }
        emitEvent(EVENT_TYPES.PRODUCT_PURCHASE);
        Loader.hide();
        return true;
      }
      if (status === 'failed') {
        if (interval) {
          clearInterval(interval);
        }
        Loader.hide();
        SnackBar.show('Payment failed try again', 'error');
        return true;
      }
      if (status === 'initiated') {
        if (interval) {
          clearInterval(interval);
        }
        Loader.hide();
        SnackBar.show('Payment cancelled try again', 'error');
        return true;
      }

    } catch (e) {
      Loader.hide();
      console.error(e);
    }
    Loader.hide();
    return false;
  }, []);

  const pollStatus = useCallback(async (pgTid, tid) => {
    if (!(await checkStatus(pgTid, tid, null))) {
      const interval = setInterval(async () => {
        checkStatus(pgTid, tid, interval);
      }, 5000);
    }
  }, [checkStatus]);

  useEffect(() => {
    if (params.has('payment')) {
      pollStatus(params.get('pgTid'), params.get('tid'));
    }
  }, []);

  const trackOrder = () => history.replace(isCustomDomain
    ? `/orders/${orderId}` : `/${shop.slug}/orders/${orderId}`);

  const goHome = () => history.replace(isCustomDomain
    ? `/` : `/${shop.slug}/`);

  const onSubmit = async () => {
    const data = {
      mode: selectedMode,
    };

    if (selectedMode.type === 'custompayment') {
      if (receipt) {
        data.receipt = receipt;
      } else {
        SnackBar.showError('Please upload a receipt');
        return;
      }
    }
    data.returnUrl = getUrl(`/pay/${link._id}?pgTid={{pgTid}}&tid={{tid}}&payment`);
    data.cancelUrl = getUrl(`/pay/${link._id}`);
    try {
      Loader.show();
      const paymentData = await payForLink(link._id, data);

      if (selectedMode.type === 'custompayment') {
        Loader.hide();
        setThankYou(true);
        return;
      }

      await Payments.initiateOnlinePayment(shop, paymentData, {
        redirect: (tid, pgTid) => {
          document.location.href = getUrl(
            `/pay/${link._id}?pgTid=${pgTid}&tid=${tid}&payment`
          );
        }
      });
    } catch (e) {
      SnackBar.showError(e);
      Loader.hide();
    }
  };

  if (thankYou) {
    return <>
      <Meta shop={shop}/>
      <ThankYou
        orderId={orderId}
        trackOrder={trackOrder}
        home={goHome}
        shop={shop}
      />
    </>;
  }

  if (params.has('payment')) {
    return (
      <>
        <Meta shop={shop}/>
        <Skeleton/>
      </>
    );
  }

  if (status === 'payment completed') {
    return <>
      <Meta shop={shop}/>
      <CustomError code={404}/>
    </>;
  }

  if (isDesktop) {
    return <div className={styles.desktopContainer}>
      <Meta shop={shop}/>
      <Container>
        <StoreHeader
          onBack={history.back}
          shop={shop}
          showSearch
          showCart
        />
      </Container>
      <Divider/>
      <Container className={styles.containerWrapper}>
        <div className={styles.body}>
          <div className={styles.paymentModeContainer}>
            <div className={styles.header}>Mode of Payment</div>
            <div className={styles.card}>
              {modes?.map((mode, index) => (
                <div key={index} className={styles.paymentCard}>
                  <div className={styles.paymentSelector} onClick={() => setSelectedMode(mode)}>
                    <p>{mode?.type === 'online' ? 'online' : getCustomModeName(mode, getCustomModes(shop, modes))}</p>
                    <div>
                      {mode?.id === selectedMode?.id ? <RadioCheckedIcon/> : <RadioIcon/>}
                    </div>
                  </div>
                  <div className={styles.body}>
                    <PaymentLinkCard
                      shop={shop}
                      modes={modes}
                      selectedMode={selectedMode}
                      isSelected={selectedMode?.id === mode?.id}
                      receipt={receipt}
                      setReceipt={setReceipt}
                    />
                  </div>
                </div>
              ))}
            </div>
          </div>
          <div className={styles.orderDetailsContainer}>
            <div className={styles.header}>Order Details</div>
            <div className={cx(styles.card, styles.orderCardContainerDesktop)}>
              <div className={styles.orderCard}>
                <div className={styles.orderDetail}>
                  <span>Order ID</span>
                </div>
                <div className={styles.orderDetail}>
                  <span className={styles.orderDetailSpacer}>:</span>
                  <span>{orderId}</span>
                </div>
              </div>
              <div className={styles.orderCard}>
                <div className={styles.orderDetail}>
                  <span>Order Value</span>
                </div>
                <div className={styles.orderDetail}>
                  <span className={styles.orderDetailSpacer}>:</span>
                  <span>{amount}</span>
                </div>
              </div>
            </div>
            <div className={styles.btnDesktop}>
              <Button
                fullWidth
                className={styles.button}
                type="submit"
                size="large"
                label="Make Payment"
                onClick={onSubmit}
              />
            </div>
          </div>
        </div>
      </Container>
    </div>;
  }

  return (
    <>
      <Meta shop={shop}/>
      <div className={styles.container}>
        <Header showBack onBack={history.back}/>
        <div className={styles.card}>
          <div className={styles.orderCard}>
            <div className={styles.orderDetail}>
              <span>Order ID</span>
            </div>
            <div className={styles.orderDetail}>
              <span className={styles.orderDetailSpacer}>:</span>
              <span>{orderId}</span>
            </div>
          </div>
          <div className={styles.orderCard}>
            <div className={styles.orderDetail}>
              <span>Order Value</span>
            </div>
            <div className={styles.orderDetail}>
              <span className={styles.orderDetailSpacer}>:</span>
              <span>{amount}</span>
            </div>
          </div>
        </div>
        <div className={styles.card}>
          <p className={styles.modeText}>Select mode of payments</p>
          {modes?.map((mode, index) => (
            <div key={index} className={styles.paymentCard}>
              <div className={styles.paymentSelector} onClick={() => setSelectedMode(mode)}>
                <p>{mode?.type === 'online' ? 'online' : getCustomModeName(mode, getCustomModes(shop, modes))}</p>
                <div>
                  {mode?.id === selectedMode?.id ? <RadioCheckedIcon/> : <RadioIcon/>}
                </div>
              </div>
              <div className={styles.body}>
                <PaymentLinkCard
                  shop={shop}
                  modes={modes}
                  selectedMode={selectedMode}
                  isSelected={selectedMode?.id === mode?.id}
                  receipt={receipt}
                  setReceipt={setReceipt}
                />
              </div>
            </div>
          ))}
        </div>
        <FooterButton>
          <Button
            fullWidth
            className={styles.button}
            type="submit"
            size="large"
            label="Make Payment"
            onClick={onSubmit}
          />
        </FooterButton>
      </div>
    </>
  );
}

PaymentLink.propTypes = {
  shop: PropTypes.object.isRequired,
  link: PropTypes.object.isRequired,
};
